var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Box, Button, IconButton, Switch, Tooltip, useMediaQuery, useTheme, Tabs, Tab, } from "@mui/material";
import { SettingRoleCard } from "@ui/components/card/SettingRoleCard";
import { CardPagination } from "@ui/components/pagination/CardPagination";
import { Add, Edit, SearchNormal } from "iconsax-react";
import TrashIcon from "@ui/components/icons/TrashIcon";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, useMatch } from "react-router-dom";
import React from "react";
import { centered } from "@ui/components/style/style";
import { PrimaryGrid } from "@ui/components/grid/PrimaryGrid";
import PrimaryChip from "@ui/components/chip/PrimaryChip";
import { PageHeader } from "@ui/components/header/PageHeader";
import { WidgetLoader } from "@ui/components/loader/WidgetLoader";
import { NoModuleAccess } from "@ui/components/no-module-access/NoModuleAccess";
import ConfirmDialog from "@ui/components/dialog/Confirmation";
import { useGetAllRolesQuery, useDeleteRoleMutation, useUpdateRoleMutation, } from "../../../features/projects/projectsApi";
import { usePermissions } from "../../../context/PermissionContext";
import { FilteredInput } from "@ui/components/inputs";
import { useSnackbar } from "notistack";
const RolePermissions = ({ isInsideSettings = false, }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const match = useMatch("/roles-permissions");
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    // Check if accessed directly (not through settings tabs)
    // If isInsideSettings prop is true, we're inside Settings component (don't show tabs)
    // Otherwise, check if we're on the roles-permissions route
    const pathname = location.pathname;
    const windowPathname = typeof window !== "undefined" ? window.location.pathname : pathname;
    // Show tabs if we're NOT inside Settings and we're on the roles-permissions route
    const isDirectAccess = !isInsideSettings &&
        (windowPathname === "/roles-permissions" ||
            windowPathname.startsWith("/roles-permissions/") ||
            match !== null ||
            pathname === "/roles-permissions" ||
            pathname.startsWith("/roles-permissions/"));
    // Tab state for direct access
    const [tabValue, setTabValue] = useState(1); // Default to Rollen & Machtigingen tab
    const handleTabChange = (event, newValue) => {
        if (newValue === 0) {
            navigate("/users");
        }
        else if (newValue === 1) {
            // Already on roles-permissions, no need to navigate
            setTabValue(1);
        }
        else if (newValue === 2) {
            navigate("/settings");
        }
    };
    // Sync tab value with current route
    useEffect(() => {
        if (isDirectAccess) {
            if (location.pathname.startsWith("/users")) {
                setTabValue(0);
            }
            else if (location.pathname.startsWith("/roles-permissions")) {
                setTabValue(1);
            }
            else if (location.pathname.startsWith("/settings")) {
                setTabValue(2);
            }
        }
    }, [location.pathname, isDirectAccess]);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 20,
    });
    const handleAddRole = () => {
        navigate("/roles-permissions/new/details");
    };
    const [searchString, setSearchString] = useState("");
    const [debouncedSearchString, setDebouncedSearchString] = useState("");
    const [switchDialogOpen, setSwitchDialogOpen] = useState(false);
    const [switchTargetRow, setSwitchTargetRow] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);
    // Permission checks
    const { canRead, canCreate, canUpdate, canDelete, isLoading: permissionsLoading, } = usePermissions();
    const { enqueueSnackbar } = useSnackbar();
    // Fetch roles from API
    const { data: roles, isLoading: rolesLoading, refetch, } = useGetAllRolesQuery();
    const [deleteRole, { isLoading: isDeletingRole }] = useDeleteRoleMutation();
    const [updateRole, { isLoading: isUpdatingRole }] = useUpdateRoleMutation();
    // Debounce search string
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchString(searchString);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchString]);
    // Transform API data for grid
    const rolesRows = (roles === null || roles === void 0 ? void 0 : roles.map((role) => ({
        id: role.id,
        role: role.name,
        users: role.usersCount,
        active: role.isActive,
        isSystem: role.name.toLowerCase() === "super admin" ||
            role.name.toLowerCase() === "superadmin",
    }))) || [];
    // Filter roles based on search
    const filteredRolesRows = rolesRows.filter((row) => {
        if (!debouncedSearchString)
            return true;
        const searchLower = debouncedSearchString.toLowerCase();
        return row.role.toLowerCase().includes(searchLower);
    });
    const rolesColumns = [
        {
            field: "role",
            type: "string",
            sortable: false,
            headerName: t("common:labels.role"),
            flex: 1,
            minWidth: 200,
        },
        {
            field: "users",
            headerName: t("common:labels.users"),
            sortable: false,
            // headerAlign: "center",
            flex: 1,
            // align: "center",
            minWidth: 200,
            renderCell: (params) => React.createElement(PrimaryChip, { label: params.row.users }),
        },
        {
            field: "active",
            headerName: t("common:labels.active"),
            // headerAlign: "center",
            flex: 1,
            // align: "center",
            minWidth: 200,
            sortable: false,
            renderCell: (params) => (React.createElement(Box, { onClick: (event) => event.stopPropagation() },
                React.createElement(Tooltip, { arrow: true, title: params.row.isSystem
                        ? "Systeemrollen kunnen niet worden gewijzigd"
                        : "Status wijzigen" },
                    React.createElement("span", null,
                        React.createElement(Switch, { size: "medium", checked: params.row.active, disabled: params.row.isSystem || !canUpdate("RolesPermissions"), onChange: () => {
                                setSwitchTargetRow(params.row);
                                setSwitchDialogOpen(true);
                            } }))))),
        },
        {
            field: "edit",
            headerName: t("common:labels.edit"),
            minWidth: 100,
            // flex: 1,
            sortable: false,
            headerAlign: "center",
            renderCell: (params) => (React.createElement(Box, { onClick: (event) => event.stopPropagation(), sx: Object.assign(Object.assign({ gap: 1 }, centered), { height: "100%", width: "100%" }) },
                canUpdate("RolesPermissions") && (React.createElement(Tooltip, { arrow: true, title: params.row.isSystem
                        ? "Systeemrollen kunnen niet worden bewerkt"
                        : "Rol bewerken" },
                    React.createElement("span", null,
                        React.createElement(IconButton, { size: "small", onClick: (event) => {
                                event.stopPropagation();
                                navigate(`/roles-permissions/${params.row.id}/details`);
                            }, disabled: params.row.isSystem, sx: {
                                opacity: params.row.isSystem ? 0.5 : 1,
                            } },
                            React.createElement(Edit, { variant: "Bulk", color: theme.palette.primary.main }))))),
                canDelete("RolesPermissions") && (React.createElement(Tooltip, { arrow: true, title: params.row.isSystem
                        ? "Systeemrollen kunnen niet worden verwijderd"
                        : "Rol verwijderen" },
                    React.createElement("span", null,
                        React.createElement(IconButton, { size: "small", onClick: (event) => {
                                event.stopPropagation();
                                setRoleToDelete(params.row);
                                setDeleteDialogOpen(true);
                            }, disabled: params.row.isSystem, sx: {
                                opacity: params.row.isSystem ? 0.5 : 1,
                            } },
                            React.createElement(TrashIcon, { color: theme.palette.error.main }))))))),
        },
    ];
    const handleSwitchRole = () => __awaiter(void 0, void 0, void 0, function* () {
        if (switchTargetRow) {
            try {
                yield updateRole({
                    roleId: switchTargetRow.id,
                    body: {
                        name: switchTargetRow.role,
                        isActive: !switchTargetRow.active,
                        permissions: null,
                    },
                }).unwrap();
                refetch();
                setSwitchDialogOpen(false);
                setSwitchTargetRow(null);
                enqueueSnackbar(switchTargetRow.active
                    ? "Rol succesvol gedeactiveerd"
                    : "Rol succesvol geactiveerd", { variant: "success" });
            }
            catch (_a) {
                setSwitchDialogOpen(false);
                setSwitchTargetRow(null);
                enqueueSnackbar("Fout bij het wijzigen van rolstatus", {
                    variant: "error",
                });
            }
        }
    });
    const handleDeleteRole = () => __awaiter(void 0, void 0, void 0, function* () {
        if (roleToDelete) {
            try {
                yield deleteRole({
                    roleId: roleToDelete.id,
                }).unwrap();
                refetch();
                setDeleteDialogOpen(false);
                setRoleToDelete(null);
                enqueueSnackbar("Rol succesvol verwijderd", {
                    variant: "success",
                });
            }
            catch (_a) {
                setDeleteDialogOpen(false);
                setRoleToDelete(null);
                enqueueSnackbar("Fout bij het verwijderen van rol", {
                    variant: "error",
                });
            }
        }
    });
    // Content for RolePermissions (without tabs)
    const rolePermissionsContent = (React.createElement(React.Fragment, null,
        React.createElement(PageHeader, { startActions: React.createElement(FilteredInput, { placeholder: "Zoeken", value: searchString, onChange: (e) => setSearchString(e.target.value), endIcon: React.createElement(SearchNormal, { size: 20, color: "currentColor" }) }), actions: React.createElement(React.Fragment, null,
                React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1 } }, isSmallScreen
                    ? canCreate("RolesPermissions") && (React.createElement(Tooltip, { title: "Rol toevoegen", arrow: true },
                        React.createElement(IconButton, { color: "primary", sx: {
                                backgroundColor: theme.palette.tertiary[40],
                                color: "white",
                                "&:hover": {
                                    backgroundColor: "primary.dark",
                                },
                            }, onClick: handleAddRole },
                            React.createElement(Add, { size: 20 }))))
                    : canCreate("RolesPermissions") && (React.createElement(Button, { startIcon: React.createElement(Add, { size: 24 }), variant: "contained", fullWidth: true, size: isSmallScreen ? "medium" : "large", color: "tertiary", onClick: handleAddRole }, t("modules:rolesPermissions.addRole"))))) }),
        permissionsLoading ? (React.createElement(Box, { sx: { height: 300 } },
            React.createElement(WidgetLoader, { isLoading: true }))) : canRead("RolesPermissions") ? (isMobile ? (React.createElement(Box, { sx: {
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
                maxWidth: "100%",
                overflowX: "hidden",
            } },
            filteredRolesRows
                .slice(paginationModel.page * paginationModel.pageSize, paginationModel.page * paginationModel.pageSize +
                paginationModel.pageSize)
                .map((row) => (React.createElement(SettingRoleCard, { key: row.id, row: row, canUpdate: canUpdate("RolesPermissions"), canDelete: canDelete("RolesPermissions"), onToggle: (r) => {
                    setSwitchTargetRow(r);
                    setSwitchDialogOpen(true);
                }, onEdit: (r) => navigate(`/roles-permissions/${r.id}/details`), onDelete: (r) => {
                    setRoleToDelete(r);
                    setDeleteDialogOpen(true);
                } }))),
            React.createElement(CardPagination, { count: filteredRolesRows.length, page: paginationModel.page, pageSize: paginationModel.pageSize, onPageChange: (newPage) => setPaginationModel((prev) => (Object.assign(Object.assign({}, prev), { page: newPage }))), onPageSizeChange: (s) => setPaginationModel((prev) => (Object.assign(Object.assign({}, prev), { pageSize: s, page: 0 }))) }))) : (React.createElement(PrimaryGrid, { rows: filteredRolesRows, columns: rolesColumns, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, paginationMode: "client", rowCount: filteredRolesRows.length, loading: rolesLoading, disableRowSelectionOnClick: true, gridHeight: "calc(100dvh - 290px)" }))) : (React.createElement(NoModuleAccess, { message: "U heeft geen toegang tot de rollen en machtigingen module" })),
        switchDialogOpen && switchTargetRow && (React.createElement(ConfirmDialog, { isOpen: switchDialogOpen, onClose: () => {
                setSwitchDialogOpen(false);
                setSwitchTargetRow(null);
            }, onConfirm: handleSwitchRole, loading: isUpdatingRole, customMessage: switchTargetRow.active
                ? t("modules:rolesPermissions.deactivationConfirm")
                : t("modules:rolesPermissions.activationConfirm") })),
        deleteDialogOpen && roleToDelete && (React.createElement(ConfirmDialog, { actionText: "Verwijderen", screenName: "Rol", isOpen: deleteDialogOpen, onClose: () => {
                setDeleteDialogOpen(false);
                setRoleToDelete(null);
            }, onConfirm: handleDeleteRole, loading: isDeletingRole }))));
    // If accessed directly, show tabs header like in settings page
    if (isDirectAccess) {
        return (React.createElement(Box, { sx: {
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
            } },
            React.createElement(Box, { sx: {
                    borderBottom: 1,
                    borderColor: "divider",
                    width: "100%",
                    backgroundColor: "background.paper",
                } },
                React.createElement(Tabs, { value: tabValue, onChange: handleTabChange, "aria-label": "settings tabs", variant: "scrollable", sx: {
                        minHeight: "48px",
                        width: "100%",
                    } },
                    React.createElement(Tab, { label: "Gebruikersbeheer" }),
                    React.createElement(Tab, { label: "Rollen & Machtigingen" }),
                    React.createElement(Tab, { label: "Bedrijfsconfiguraties" }))),
            React.createElement(Box, { sx: { width: "100%" } }, rolePermissionsContent)));
    }
    // If accessed through settings tabs, just return the content without tabs
    return rolePermissionsContent;
};
export default RolePermissions;
